import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Wrench, Plus, Trash2, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

export default function Tasks({ tasks }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  function add(e) {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    router.post('/service-schedule/tasks', { name }, {
      preserveScroll: true,
      onSuccess: () => { setName(''); toast.success('Check added') },
      onError: () => toast.error('Could not add'),
      onFinish: () => setBusy(false),
    })
  }

  function toggleActive(t) {
    router.patch(`/service-schedule/tasks/${t.id}`, { active: !t.active }, {
      preserveScroll: true,
      onError: () => toast.error('Could not update'),
    })
  }

  function remove(t) {
    if (!confirm(`Remove "${t.name}" from the monthly checklist? Past sign-offs are kept.`)) return
    router.delete(`/service-schedule/tasks/${t.id}`, {
      preserveScroll: true,
      onSuccess: () => toast.success('Check removed'),
      onError: () => toast.error('Could not remove'),
    })
  }

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-2xl">
        <Link href="/service-schedule" className="mb-3 inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-ink">
          <ArrowLeft size={15} /> Back to schedule
        </Link>
        <div className="mb-5 flex items-center gap-3">
          <Wrench className="text-brand-primary" />
          <div>
            <h1 className="text-xl font-semibold text-brand-ink">Edit monthly checklist</h1>
            <p className="text-sm text-brand-muted">Add, rename or hide the equipment checks staff sign off each month.</p>
          </div>
        </div>

        <form onSubmit={add} className="mb-6 flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New check, e.g. Calibrate intra-oral scanner"
            className="flex-1 rounded-xl border border-brand-border bg-white px-3 py-2.5 text-sm focus:border-brand-primary focus:outline-none" />
          <button type="submit" disabled={busy}
            className="flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            <Plus size={16} /> Add
          </button>
        </form>

        <div className="divide-y divide-brand-border overflow-hidden rounded-2xl border border-brand-border bg-white">
          {tasks.map((t) => (
            <div key={t.id} className={cn('flex items-center gap-3 px-4 py-3', !t.active && 'opacity-50')}>
              <input
                defaultValue={t.name}
                onBlur={(e) => {
                  const v = e.target.value.trim()
                  if (v && v !== t.name) router.patch(`/service-schedule/tasks/${t.id}`, { name: v }, { preserveScroll: true })
                }}
                className="min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 py-1 text-sm font-medium text-brand-ink hover:border-brand-border focus:border-brand-primary focus:bg-white focus:outline-none" />
              {!t.active && <span className="rounded-full bg-brand-surface px-2 py-0.5 text-xs text-brand-muted">hidden</span>}
              <button onClick={() => toggleActive(t)} title={t.active ? 'Hide from checklist' : 'Show on checklist'}
                className="rounded-lg p-1.5 text-brand-muted hover:bg-brand-surface hover:text-brand-ink">
                {t.active ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={() => remove(t)} title="Remove"
                className="rounded-lg p-1.5 text-brand-muted hover:bg-brand-danger/10 hover:text-brand-danger">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {tasks.length === 0 && <p className="px-4 py-6 text-center text-sm text-brand-muted">No checks yet — add one above.</p>}
        </div>
      </div>
    </DashboardLayout>
  )
}
